/* @flow */

/*
 * this is a little temporary store for the poll that is currently being edited
 * (that is "checkedout").
 * the checkedout poll is stored here in order to not interfere with the PollsStore.
 * a checkedout poll is only saved to the PollsStore when it got saved to the server.
 */

import { omit } from 'lodash'

import type {
  RootState,
  Action,
  Poll,
  CheckedoutPoll,
  PollTypeId,
  CustomerId,
} from '../types'

import { ActionRequiresPropertyError } from '../helpers'

import { selectCheckedoutExpertOpinions } from './pollBuilderCheckedoutExpertOpinions'

import { getFreshPoll } from '../stores/pollsStore'

import {
  CHECKOUT_EXISTING_POLL,
  CHECKOUT_NEW_POLL,
  UPDATE_CHECKEDOUT_POLL,
} from './pollBuilder'

export const UPLOAD_RANKING_TOOL_IMAGE_START = 'UPLOAD_RANKING_TOOL_IMAGE_START'
export const UPLOAD_RANKING_TOOL_IMAGE_SUCCESS =
  'UPLOAD_RANKING_TOOL_IMAGE_SUCCESS'
export const UPLOAD_RANKING_TOOL_IMAGE_ERROR = 'UPLOAD_RANKING_TOOL_IMAGE_ERROR'

export const STATE_KEY = 'pollBuilderCheckedoutPoll'

export function titleToId(header: string, customer: string): string {
  if (header === '') {
    return header
  }
  return [
    header
      .toLowerCase()
      .split(' ')
      .map(str => str.replace(/[^0-9a-z]/gi, ''))
      .join('-'),
    customer.toLowerCase(),
  ].join('_')
}

export function uploadRankingToolImageStart(
  optionId: string,
  blob: string,
  description: string
): Action {
  return {
    type: UPLOAD_RANKING_TOOL_IMAGE_START,
    optionId,
    blob,
    description,
  }
}

export function uploadRankingToolImageSuccess(
  optionId: string,
  url: string
): Action {
  return {
    type: UPLOAD_RANKING_TOOL_IMAGE_SUCCESS,
    optionId,
    url,
  }
}

export function uploadRankingToolImageError(
  optionId: string,
  error: Error
): Action {
  return {
    type: UPLOAD_RANKING_TOOL_IMAGE_ERROR,
    error,
  }
}

export function checkoutExistingPoll(item: Poll): Action {
  return {
    type: CHECKOUT_EXISTING_POLL,
    item,
  }
}

export function checkoutNewPoll(
  pollTypeId: PollTypeId,
  customerId: ?CustomerId
): Action {
  return {
    type: CHECKOUT_NEW_POLL,
    pollTypeId,
    customerId,
  }
}

// update the poll that is currently checked out
export function updateCheckedoutPoll(changes: CheckedoutPoll): Action {
  return {
    type: UPDATE_CHECKEDOUT_POLL,
    changes,
  }
}

export function selectIsPollCheckedout(state: RootState): boolean {
  return state[STATE_KEY] != null
}

export function isPollNew(poll: any) {
  return poll.id == null
}

export function selectIsCheckedoutPollNew(state: RootState): boolean {
  return state[STATE_KEY] != null && isPollNew(state[STATE_KEY])
}

export function selectCheckedoutPoll(state: RootState): ?CheckedoutPoll {
  const poll = state[STATE_KEY]
  if (poll == null) return null
  return Object.assign({}, poll, {
    expertOpinions: selectCheckedoutExpertOpinions(state),
  })
}

export default function reducer(
  state: ?CheckedoutPoll = null,
  action: Object
): ?CheckedoutPoll {
  switch (action.type) {
    case CHECKOUT_EXISTING_POLL:
      if (action.item == null) {
        throw new ActionRequiresPropertyError(CHECKOUT_EXISTING_POLL, 'item')
      }
      return omit(action.item, 'expertOpinions')

    case CHECKOUT_NEW_POLL:
      if (action.pollTypeId == null) {
        throw new ActionRequiresPropertyError(CHECKOUT_NEW_POLL, 'pollTypeId')
      }
      return getFreshPoll(action.pollTypeId, action.customerId || null)

    case UPDATE_CHECKEDOUT_POLL:
      if (action.changes == null) {
        throw new ActionRequiresPropertyError(UPDATE_CHECKEDOUT_POLL, 'changes')
      }
      const additionalChanges = {}
      if (
        state &&
        !state.userEnteredId &&
        (action.changes.header || action.changes.header === '') &&
        (action.changes.customer || state.customer)
      ) {
        // for new polls auto generate name

        const customer =
          action.changes.customer || state.customer || 'production'
        const autogeneratedName = titleToId(action.changes.header, customer)
        if (isPollNew(state)) {
          additionalChanges.name = autogeneratedName
        }
      }

      if (action.changes == null) {
        throw new ActionRequiresPropertyError(UPDATE_CHECKEDOUT_POLL, 'changes')
      }

      return {
        ...state,
        ...omit(action.changes, 'expertOpinions'),
        ...additionalChanges,
      }

    case UPLOAD_RANKING_TOOL_IMAGE_SUCCESS:
      if (action.optionId == null) {
        throw new ActionRequiresPropertyError(
          UPLOAD_RANKING_TOOL_IMAGE_SUCCESS,
          'optionId'
        )
      }
      if (!action.url) {
        throw new ActionRequiresPropertyError(
          UPLOAD_RANKING_TOOL_IMAGE_SUCCESS,
          'url'
        )
      }

      const { optionId, url }: { optionId: string, url: string } = action

      if (!state || state.options == null)
        throw new Error('Poll has no options')
      const { options } = state
      return {
        ...state,
        options: options.map(option => {
          if (option.optionId !== optionId) {
            return option
          }
          return {
            ...option,
            image: url,
          }
        }),
      }
    default:
      return state
  }
}

export function getPollCustomerNicheAutomation(state: RootState): boolean {
  const store = state.customersStore
  const poll = state.pollBuilderCheckedoutPoll
  if (!store || !poll || !poll.customer || !store[poll.customer]) {
    return false
  }
  return store[poll.customer].hasNicheAutomation ? true : false
}



// WEBPACK FOOTER //
// ./src/stores/pollBuilderCheckedoutPoll.js